import React from "react";
import { useLocation } from "react-router-dom";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import {
  Button,
  Typography,
  List as MuiList,
  Box,
  Chip,
} from "@mui/material";
import { Description, Home } from "@mui/icons-material";
import SidebarListItem from "components/common/navigation/sidebar-list-item";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { Viewer } from "components/auth/types";
import { useFeature } from "components/feature";
import { Feature } from "types/gql-types";
import {
  mergePropertyContractSidebarUpdatedIntoList,
  PROPERTY_CONTRACT_SIDEBAR_UPDATED_SUBSCRIPTION,
} from "data/graphql/property-contract-sidebar-updated-subscription";
import {
  mergeSettingsSidebarInterestListAddedIntoOrgLists,
  SETTINGS_SIDEBAR_INTEREST_LIST_ADDED_SUBSCRIPTION,
} from "data/graphql/settings-sidebar-interest-list-added-subscription";
import { List, sortAndFilterLists } from "./list-utils";
import {
  buildSidebarEntries,
  listSidebarHref,
  sidebarChipForContract,
  sidebarChipForList,
  SidebarEntry,
} from "./sidebar-entries";

type Data = {
  organization: {
    id: string;
    interestLists: List[];
  };
};

const ORG_LISTS = gql`
  query getOrgLists($orgId: ID!) {
    organization(id: $orgId) {
      id
      interestLists {
        id
        name
        property {
          id
          isDraft
        }
      }
    }
  }
`;

type PropertyContractsSidebarData = {
  propertyContractsSidebar: Array<{
    id: string;
    propertyName: string | null;
    status: string | null;
    interestListId: string | null;
  }>;
};

const CONTRACTS_SIDEBAR_QUERY = gql`
  query PropertyContractsSidebar {
    propertyContractsSidebar {
      id
      propertyName
      status
      interestListId
    }
  }
`;

const SidebarChip: React.FC<{ label: string; color: "info" | "warning" }> = ({ label, color }) => (
  <Chip size="small" variant="outlined" color={color} label={label} />
);

const entryLabel = (name: string) => (
  <Typography component="span" variant="body1" noWrap title={name}>
    {name}
  </Typography>
);

const ListSidebarRow: React.FC<{
  entry: Extract<SidebarEntry, { kind: "list" }>;
  currentPath: string;
}> = ({ entry, currentPath }) => {
  const { list, contractStatus } = entry;
  const chip = sidebarChipForList(list, contractStatus);
  const href = listSidebarHref(list.id, contractStatus);
  return (
    <SidebarListItem
      icon={<Home />}
      text={(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {entryLabel(list.name)}
          {chip && <SidebarChip label={chip.label} color={chip.color} />}
        </Box>
      )}
      selected={currentPath.includes(list.id)}
      href={href}
    />
  );
};

const OrphanSentSidebarRow: React.FC<{
  entry: Extract<SidebarEntry, { kind: "orphan-sent" }>;
  currentPath: string;
}> = ({ entry, currentPath }) => {
  const { contract } = entry;
  const name = contract.propertyName || "Untitled Property";
  const chip = sidebarChipForContract(contract.status);
  return (
    <SidebarListItem
      icon={<Description />}
      text={(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {entryLabel(name)}
          {chip && <SidebarChip label={chip.label} color={chip.color} />}
        </Box>
      )}
      selected={currentPath.includes(`/contracts/${contract.id}`)}
      href={`/dash/manager/settings/contracts/${contract.id}`}
    />
  );
};

const AddPropertyButton: React.FC = () => {
  const planSelectionEnabled = useFeature(Feature.PLAN_SELECTION);
  if (!planSelectionEnabled) return null;
  return (
    <div style={{ padding: 8 }}>
      <Button fullWidth variant="contained" color="primary" href="/dash/manager/settings/property/new/choose">
        Add New Property
      </Button>
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
        New properties are billed at $3/unit/month
      </Typography>
    </div>
  );
};

const ListsSidebar: React.FC<{ viewer: Viewer }> = ({ viewer }) => {
  const orgId = viewer.organizationId;
  const planSelectionEnabled = useFeature(Feature.PLAN_SELECTION);
  const location = useLocation();
  const client = useApolloClient();

  const {
    loading: listsLoading,
    error: listsError,
    data: listsData,
    subscribeToMore: subscribeToMoreLists,
  } = useQuery<Data>(ORG_LISTS, {
    fetchPolicy: "cache-first",
    variables: { orgId },
    skip: !orgId,
  });

  React.useEffect(() => {
    if (!orgId) {
      return () => {};
    }
    const unsubscribe = subscribeToMoreLists({
      document: SETTINGS_SIDEBAR_INTEREST_LIST_ADDED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        client.refetchQueries({ include: ["getViewer"] }).catch(() => {});
        return mergeSettingsSidebarInterestListAddedIntoOrgLists(prev, subscriptionData);
      },
    });
    return () => {
      unsubscribe();
    };
  }, [client, orgId, subscribeToMoreLists]);

  const {
    loading: contractsLoading,
    error: contractsError,
    data: contractsData,
    subscribeToMore: subscribeToMoreContracts,
  } = useQuery<PropertyContractsSidebarData>(CONTRACTS_SIDEBAR_QUERY, {
    fetchPolicy: "cache-first",
    skip: !orgId || !planSelectionEnabled,
  });

  React.useEffect(() => {
    if (!planSelectionEnabled) {
      return () => {};
    }
    const unsubscribe = subscribeToMoreContracts({
      document: PROPERTY_CONTRACT_SIDEBAR_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) =>
        mergePropertyContractSidebarUpdatedIntoList(prev, subscriptionData),
    });
    return () => {
      unsubscribe();
    };
  }, [planSelectionEnabled, subscribeToMoreContracts]);

  if (!orgId) return null;

  if (listsError) return <ServerError desc={listsError.message} />;
  if (listsLoading) return <LoadingSpinner />;
  if (!listsData) return <ServerError desc="Missing Data" />;

  const lists = sortAndFilterLists(viewer, listsData.organization.interestLists);
  const contracts = contractsData?.propertyContractsSidebar || [];
  const sidebarEntries = planSelectionEnabled ? buildSidebarEntries(lists, contracts) : lists.map((list) => ({
    kind: "list" as const,
    sortName: list.name,
    list,
    contractStatus: null,
  }));

  const position = lists.length > 5 ? "top" : "bottom";

  return (
    <div>
      <div>
        {position === "top" && <AddPropertyButton />}
        <MuiList>
          {sidebarEntries.map((entry) => (
            entry.kind === "list" ? (
              <ListSidebarRow
                key={`list-${entry.list.id}`}
                entry={entry}
                currentPath={location.pathname}
              />
            ) : (
              <OrphanSentSidebarRow
                key={`contract-${entry.contract.id}`}
                entry={entry}
                currentPath={location.pathname}
              />
            )
          ))}
        </MuiList>
        {planSelectionEnabled && contractsError && (
          <Typography variant="caption" color="error" sx={{ display: "block", px: 1 }}>
            Failed to load contracts
          </Typography>
        )}
        {planSelectionEnabled && contractsLoading && !contractsData && (
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", px: 1 }}>
            Loading contracts…
          </Typography>
        )}
        {position === "bottom" && <AddPropertyButton />}
      </div>
    </div>
  );
};

export default ListsSidebar;
